<template>
  <CLoading :loading="loading" />
  <q-form ref="formRef" greedy>
    <div class="">
      <div class="row justify-between horizontal-line">
        <div class="text-h5 text-bold q-py-xs q-px-sm q-ml-sm horizontal-line">リッチメニュー設定</div>
        <div>
          <TestModeSelector v-model="isTestMode" @update:detailCopy="onDetailCopy" v-if="form._id !== ''" />
        </div>
      </div>
      <div class="text-caption q-my-md">複数のメニューアセットを追加することで、メニューの切替が可能です。</div>
      <div class="row q-gutter-md">
        <div class="col-4">
          <label class="text-grey-9 text-bold">{{ $t('label.rich_menu.name') }}</label>
          <q-input
            lazy-rules
            :rules="[(val) => (val && val.length > 0) || requiredRule.name]"
            outlined
            v-model="form.name"
            dense
            :placeholder="$t('label.rich_menu.name')"
          >
          </q-input>
        </div>
        <div class="col-4">
          <label class="text-grey-9 text-bold">{{ $t('label.rich_menu.chat_bar_text') }}</label>
          <q-input
            lazy-rules
            :rules="[(val) => (val && val.length > 0) || requiredRule.chatBarText]"
            outlined
            v-model="form.test_detail.chat_bar_text"
            dense
            :placeholder="$t('label.rich_menu.chat_bar_text')"
            maxlength="14"
            v-if="isTestMode"
          >
          </q-input>
          <q-input
            lazy-rules
            :rules="[(val) => (val && val.length > 0) || requiredRule.chatBarText]"
            outlined
            v-model="form.prod_detail.chat_bar_text"
            dense
            :placeholder="$t('label.rich_menu.chat_bar_text')"
            maxlength="14"
            v-if="!isTestMode"
          >
          </q-input>
        </div>
      </div>
      <div class="horizontal-line"></div>
      <div class="row q-my-sm">
        <div class="col q-px-sm vertical-line">
          <RichMenuSelector
            ref="richMenuSelector"
            v-if="isTestMode"
            v-model="form.test_detail.rich_menu_content"
            @update:submitMenu="onSubmit(false)"
            @update:refetchMenu="refetchMenu"
            @onPreview="onPreview"
          />
          <RichMenuSelector
            ref="richMenuSelector"
            v-if="!isTestMode"
            v-model="form.prod_detail.rich_menu_content"
            @update:submitMenu="onSubmit(false)"
            @update:refetchMenu="refetchMenu"
            @onPreview="onPreview"
          />
        </div>
        <div class="col-3 q-px-sm">
          <div class="row q-pa-sm justify-between items-center horizontal-line" style="height: 50px">
            <div class="text-caption text-bold">プレビュー</div>
          </div>
          <div class="flex justify-center">
            <div style="width: 360px">
              <div style="max-width: 360px">
                <div class="q-pa-md">
                  <div class="preview-device--sp row justify-center">
                    <div class="preview-device--container" ref="pageChat">
                      <div class="full-width flex justify-center" v-if="menuImageUrl">
                        <img :src="menuImageUrl" style="max-width: 100%; object-fit: contain" alt="" />
                      </div>
                    </div>
                    <div class="preview-device--menu" v-if="menuImageUrl">
                      <div class="full-height relative-position flex items-center justify-center">
                        <q-icon name="keyboard" color="white" size="sm" style="position: absolute; left: 12px" />
                        <div class="text-white" style="font-size: 20px">
                          {{ isTestMode ? form.test_detail.chat_bar_text : form.prod_detail.chat_bar_text }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="horizontal-line"></div>
    </div>
    <q-space class="q-my-md" />
    <div class="row no-wrap items-center">
      <q-space />
      <div class="q-gutter-sm">
        <q-btn no-caps class="btn-cancel" @click="onCancel">{{ $t('cancel') }}</q-btn>
        <q-btn no-caps class="btn-save" @click="onSubmit"> {{ $t('save') }} </q-btn>
      </div>
    </div>
  </q-form>
</template>

<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import TestModeSelector from '@/components/common/ui/TestModeSelector.vue'
import RichMenuSelector from '@/components/rich-menus/selectors/RichMenuSelector.vue'
import { maska } from 'maska'
import { IRichMenu, IRichMenuContent } from '@/utils/types'
import { NumberUtils } from '@/utils/number'
import { constant } from '@/utils/constants'
import { ACTION_RICH_MENU } from '@/store/actions'
import BaseFormMixin from '@/components/common/mixins/BaseFormMixin.vue'
import cloneDeep from 'lodash/cloneDeep'
import { Watch } from 'vue-property-decorator'
import CLoading from '@/components/common/ui/CLoading.vue'

@Options({
  components: { CLoading, RichMenuSelector, TestModeSelector },
  directives: { maska },
  async beforeRouteLeave() {
    if (!this) {
      return
    }
    const result = await this.checkDraftValue()
    if (result) {
      return true
    } else {
      return false
    }
  },
})
export default class RichMenuSettingForm extends mixins(BaseFormMixin) {
  isTestMode = false
  loading = false
  menuImageUrl = ''

  form: IRichMenu = {
    _id: '',
    name: '',
    app_id: '',
    is_active: false,
    is_test_mode: false,
    prod_detail: {
      chat_bar_text: '',
      rich_menu_content: [
        {
          label: '',
          rich_menu_alias_id: NumberUtils.uniqueId(),
          size: {
            width: constant.RICH_MENU_IMAGE.MAX_IMAGE_WIDTH,
            height: constant.RICH_MENU_IMAGE.MAX_IMAGE_HEIGHT,
          },
          tappable_area: [],
          image_url: '',
        },
      ],
    },
    test_detail: {
      chat_bar_text: '',
      rich_menu_content: [
        {
          label: '',
          rich_menu_alias_id: NumberUtils.uniqueId(),
          size: {
            width: constant.RICH_MENU_IMAGE.MAX_IMAGE_WIDTH,
            height: constant.RICH_MENU_IMAGE.MAX_IMAGE_HEIGHT,
          },
          tappable_area: [],
          image_url: '',
        },
      ],
    },
  }

  get selectedAppId() {
    return this.$route.params.app_id as string
  }

  get selectedRichMenuId() {
    return this.$route.params.rich_menu_id || ''
  }

  get actionType() {
    return this.$route.params.action_type || ''
  }

  get requiredRule() {
    const requiredRule = {
      name: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.rich_menu.name'),
      }),

      chatBarText: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.rich_menu.chat_bar_text'),
      }),
      messageImage: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.common.messages.image'),
      }),
    }
    return requiredRule
  }

  onDetailCopy() {
    if (this.isTestMode) {
      this.form.test_detail = this.form.prod_detail
    } else {
      this.form.prod_detail = this.form.test_detail
    }
  }

  onCancel() {
    this.goto('rich_menus', { app_id: this.selectedAppId })
  }

  onSubmit(isBack = true) {
    this.$refs.formRef
      .validate()
      .then(async (success: boolean) => {
        if (!success) {
          return
        }

        await this.doSave(isBack)
      })
      .catch((error: unknown) => {
        console.log('error', error)
      })
  }

  async doSave(isBack = true) {
    this.loading = true
    const { dispatch } = this.$store
    let item = null
    if (!this.form._id) {
      this.form.is_active = false
      this.form.app_id = this.selectedAppId
      this.form.test_detail = this.form.prod_detail
      item = await dispatch(ACTION_RICH_MENU.ADD, {
        ...this.form,
      })
    } else {
      item = await dispatch(ACTION_RICH_MENU.UPDATE, {
        ...this.form,
      })
    }

    if (item) {
      this.loading = false
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })

      this.initForm = cloneDeep(this.parentForm)
      if (isBack) {
        this.onCancel()
      } else {
        this.$refs.richMenuSelector.updateOriginalMenus()
      }
    } else {
      this.loading = false
    }
  }

  goto(name: string, params = {}) {
    this.$router.push({
      name,
      params,
    })
  }

  @Watch('isTestMode', { immediate: true })
  onPreview(data: IRichMenuContent) {
    this.menuImageUrl = data.image_url ?? ''
  }

  @Watch('actionType', { immediate: true })
  @Watch('selectedRichMenuId', { immediate: true })
  async fetchRichMenu() {
    if (!this.selectedAppId) {
      return
    }

    if (this.selectedRichMenuId !== '') {
      const data: IRichMenu = await this.$store.dispatch(ACTION_RICH_MENU.LOAD_ITEM, this.selectedRichMenuId)
      if (data) {
        this.form = { ...this.form, ...data }
        if (this.actionType === constant.ACTION_TYPE.COPY) {
          this.form._id = ''
          this.form.name = this.form.name + ' Copy'
          this.form.is_test_mode = false
          this.form.is_active = false
          this.form.prod_rich_menu_alias_ids = []
          this.form.prod_rich_menu_ids = []
          this.form.test_rich_menu_alias_ids = []
          this.form.test_rich_menu_ids = []
          if (this.form.prod_detail?.rich_menu_content) {
            for (const item of this.form.prod_detail?.rich_menu_content) {
              item.rich_menu_alias_id = NumberUtils.uniqueId()
            }
          }

          if (this.form.test_detail?.rich_menu_content) {
            for (const item of this.form.test_detail?.rich_menu_content) {
              item.rich_menu_alias_id = NumberUtils.uniqueId()
            }
          }
        }
      }
      this.parentForm = this.form as any
      this.initForm = cloneDeep(this.parentForm)
    }
  }

  refetchMenu(data: IRichMenuContent) {
    setTimeout(() => {
      if (this.isTestMode) {
        if (this.form.test_detail?.rich_menu_content) {
          this.form.test_detail.rich_menu_content.push(data)
        }
      } else {
        if (this.form.prod_detail?.rich_menu_content) {
          this.form.prod_detail.rich_menu_content.push(data)
        }
      }
    }, 500)
  }

  async created() {
    await this.fetchRichMenu()

    if (this.selectedRichMenuId === '') {
      this.$nextTick(() => {
        this.$refs.formRef.validate()
      })
    }
  }
}
</script>

<style scoped lang="scss">
.text-h5.horizontal-line {
  border-bottom: 1px solid black;
}

.preview-device {
  position: relative;
  $root: &;
  &--sp {
    overflow: hidden;
    position: relative;
    background: url('/img/devices/iphone_14.png') no-repeat center;
    background-size: contain;
    margin: auto;
    height: 80%;
    aspect-ratio: 421 / 850;
    top: 8px;
    #{$root} {
      &--container {
        border: 18px solid transparent;
        border-top-width: 50px;
      }
    }

    .preview-device--popup--overlay {
      width: 100%;
    }
  }
  &--container {
    margin: 0 auto;
    overflow: auto;
    width: 100%;
    height: 88%;
    position: relative;
    display: flex;
    align-items: flex-end;
    &:after {
      position: absolute;
      content: '';
      width: 100%;
      height: 100%;
    }
  }
  &--menu {
    position: absolute;
    bottom: 2.4%;
    width: 89%;
    height: 12%;
    background: #34425f;
    border-radius: 0 0 28px 28px;
  }
}
</style>
